import { FC } from 'react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, PopoverMenuItem, Text, usePopoverMenu } from '@frontend/design-system';
import { SelfUpgradeActionType } from '../../../constants/self-upgrade';
import { getNotificationTeaserTrackingId } from '../../../constants/tracking-ids';
import { useFeatureSubscription } from '../../../providers/FeatureSubscriptionProvider';
import { getActionIconName, getActionText, getNotificationDismissActionText } from '../../../self-upgrade.util';

interface PromoActionsProps {
  disabled?: boolean;
  dismissActions: PlatformLocationFeatureV1.Types.Action[];
  featureEnum: Feature;
  primaryAction: SelfUpgradeActionType;
  source: PlatformLocationFeatureV1.Types.Source;
  onDismissAction: (action: PlatformLocationFeatureV1.Types.Action) => void;
  onUpgradeAction: () => void;
}

export const PromoActions: FC<PromoActionsProps> = ({
  disabled,
  dismissActions,
  featureEnum,
  primaryAction,
  source,
  onDismissAction,
  onUpgradeAction,
}) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'online-scheduling-promo' });
  const { isBundleUpgradeEligible } = useFeatureSubscription();

  const { getTriggerProps, getItemProps, getMenuProps } = usePopoverMenu({
    placement: 'top-start',
  });

  return (
    <>
      <div css={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: theme.spacing(1) }}>
        <Button
          variant='tertiary'
          color='primary'
          {...getTriggerProps()}
          disabled={disabled}
          trackingId={getNotificationTeaserTrackingId({
            featureEnum,
            source,
            upgradeType: isBundleUpgradeEligible ? 'bundle' : 'feature',
            dismissAction: 'DISMISS',
          })}
        >
          <Text color='light'>{t('Dismiss')}</Text>
        </Button>
        <Button
          variant='primary'
          onClick={onUpgradeAction}
          trackingId={getNotificationTeaserTrackingId({
            featureEnum,
            source,
            upgradeType: isBundleUpgradeEligible ? 'bundle' : 'feature',
            upgradeAction: primaryAction,
          })}
          disabled={disabled}
        >
          <Icon name={getActionIconName(primaryAction)} size={16} />
          {getActionText(primaryAction, featureEnum).primaryButtonText}
        </Button>
      </div>
      <PopoverMenu {...getMenuProps()}>
        {dismissActions.map((dismissAction, index) => (
          <PopoverMenuItem
            key={dismissAction}
            {...getItemProps({
              index,
              onClick: () => onDismissAction(dismissAction),
            })}
            trackingId={getNotificationTeaserTrackingId({
              featureEnum,
              source,
              upgradeType: isBundleUpgradeEligible ? 'bundle' : 'feature',
              dismissAction,
            })}
          >
            {getNotificationDismissActionText(dismissAction)}
          </PopoverMenuItem>
        ))}
      </PopoverMenu>
    </>
  );
};
